// Q9 S7 — focus + scroll an input into view by its server-side API field
// name (the same key used in the 422 error map). Sections opt in by
// rendering inputs with `data-api-field="<name>"` (FormField/SelectField
// forward `apiFieldName` to that attribute) and calling this hook with
// their container ref + the desired focus target.
//
// useLayoutEffect (not useEffect): so the focus call lands in the same
// browser frame the section's DOM commits, avoiding a flash where the
// section is visible but the cursor hasn't moved.

import { useLayoutEffect } from "react";

const FOCUSABLE_SELECTOR_ATTRS = "input, select, textarea";

export function useFocusOnApiField(
  // RefObject<HTMLElement | null> — matches what `useRef<HTMLDivElement>(null)`
  // produces under React 19's narrowed RefObject type.
  containerRef: React.RefObject<HTMLElement | null>,
  apiFieldName: string | null | undefined,
): void {
  useLayoutEffect(() => {
    if (!apiFieldName || !containerRef.current) return;
    const container = containerRef.current;
    // The data-api-field attribute is on the input/select/textarea itself,
    // so we can match directly. CSS.escape so dotted/bracketed field names
    // (medication_adherence_log.0.drug_name) survive the selector pass.
    const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(apiFieldName) : apiFieldName;
    const selector = FOCUSABLE_SELECTOR_ATTRS
      .split(", ")
      .map((tag) => `${tag}[data-api-field="${escaped}"]`)
      .join(", ");
    const el = container.querySelector<HTMLElement>(selector);
    if (!el) return;
    el.focus({ preventScroll: true });
    // scrollIntoView is optional — jsdom (and a handful of older browsers)
    // don't ship it. The focus call alone is the correctness guarantee;
    // scrolling is a UX nicety that should never throw if absent.
    el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [apiFieldName, containerRef]);
}
